import { Component, computed, input, output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { formatDateKey, isCanadaRegion } from '../dashboard-dates.util';
import { previewText, type DashboardVehicleTile } from '../dashboard.types';

/**
 * Order guide summary for one vehicle. Live Date is derived from Active Date and stays read-only.
 * Active Date, Send for Translation (Canada), SORP, Notes and Attachments all open the same dialog.
 */
@Component({
  selector: 'app-order-guide-tile',
  imports: [MatTooltipModule, RouterLink],
  templateUrl: './order-guide-tile.component.html',
  styleUrl: './order-guide-tile.component.scss'
})
export class OrderGuideTileComponent {
  readonly tile = input.required<DashboardVehicleTile>();

  readonly edit = output<DashboardVehicleTile>();
  readonly copyVehicle = output<DashboardVehicleTile>();

  readonly vehicleDetailLink = computed(() => ['/maintenance', this.tile().vehicle.id]);

  readonly activeDate = computed(() => formatDateKey(this.tile().activeDate));
  readonly liveDate = computed(() => formatDateKey(this.tile().liveDate));
  readonly sorpDate = computed(() => formatDateKey(this.tile().sorpDate));

  readonly showTranslationDate = computed(() => isCanadaRegion(this.tile().vehicle.regionName));

  /** Null outside Canada, where there is no translation deadline. */
  readonly translationDate = computed(() => formatDateKey(this.tile().translationDate));

  readonly notesPreview = computed(() => {
    const notes = this.tile().notes;
    if (!notes.length) {
      return 'None';
    }
    if (notes.length === 1) {
      return previewText(notes[0].text);
    }
    return `${notes.length} notes`;
  });

  readonly notesTooltip = computed(() =>
    this.tile()
      .notes.map((n) => n.text.trim())
      .filter(Boolean)
      .join('\n\n')
  );

  readonly attachmentsPreview = computed(() => {
    const attachments = this.tile().attachments;
    if (!attachments.length) {
      return 'None';
    }
    if (attachments.length === 1) {
      return previewText(attachments[0].fileName);
    }
    return `${attachments.length} files`;
  });

  onEdit(): void {
    this.edit.emit(this.tile());
  }

  onCopyVehicle(): void {
    this.copyVehicle.emit(this.tile());
  }
}
